import React, { useRef, useState, useCallback, useEffect } from 'react';
import FJVideoBox from './FJVideoBox/FJVideoBox';
import FJPlayController from './playController/FJController';
import FJUtil from '../../../util/FJUtil.js';
import FJVideoUtil from '../../../util/FJVideoUtil.js';

const FJVideoContent = props => {
    const {
        url,
        duration,
        isAutoPlay = false,
        onError,
        isLoadedError,
        viewSize,
        renderBottom,
        isLoop = false,
        ratio,
        style,
        scope = 'modal',
        isKeySpace = true,
        isShowTransparentBG = false,
        hideController = false,
        isShowFullScreen,
        ignoreViewSize = false,
    } = props;
    const [isLoaded, setIsLoaded] = useState(false);
    const [videoUrl, setVideoUrl] = useState(url);
    const [videoDuration, setVideoDuration] = useState(duration);

    const videoBoxRef = useRef(null);
    const playCrlEle = useRef(null);
    const _timer = useRef(null);

    const _clearProgressTimer = useCallback(() => {
        if (_timer.current) {
            clearInterval(_timer.current);
        }
    }, []);

    useEffect(() => {
        if (playCrlEle.current) {
            playCrlEle.current.reset();
        }
        setVideoUrl(url);
        setIsLoaded(false);
        _clearProgressTimer();
    }, [url]);

    const _updateProgress = () => {
        _clearProgressTimer();
        _timer.current = setInterval(() => {
            try {
                const currentTime = videoBoxRef.current?.getVideo().currentTime;
                const duration = videoBoxRef.current?.getVideo().duration;
                const progress = Math.min(currentTime / duration, 1);
                playCrlEle.current?.updateProgress(progress);
            } catch (e) {
                console.log(e);
                _clearProgressTimer();
            }
        }, 40);
    };

    const _pause = useCallback(() => {
        videoBoxRef.current.pause();
        if (playCrlEle.current) {
            playCrlEle.current.changePlayState(false);
        }
        _clearProgressTimer();
    }, [_clearProgressTimer]);

    const _play = useCallback(() => {
        videoBoxRef.current.play();
        if (playCrlEle.current) {
            playCrlEle.current.changePlayState(true);
        }
        _updateProgress();
    }, [_updateProgress, videoBoxRef.current, playCrlEle.current, videoDuration]);

    const _endCallback = useCallback(() => {
        if (playCrlEle.current) {
            if (isLoop) {
                videoBoxRef.current.seek(0);
                _play();
            } else {
                _pause();
                playCrlEle.current.reset();
            }
        }
    }, [_pause, playCrlEle.current]);

    const _videoLoaded = useCallback(() => {
        setIsLoaded(true);
        const video = videoBoxRef.current.getVideo();
        setVideoDuration(video.duration);

        if (isAutoPlay) {
            videoBoxRef.current.seek(0);
            _play();
        }
    }, [_play, isAutoPlay]);

    const _sliderDataChange = useCallback(
        data => {
            const time = videoDuration * data;
            videoBoxRef.current.seek(time);
        },
        [videoDuration],
    );
    const _volumeChange = useCallback(value => {
        videoBoxRef.current.setVolume(value);
    }, []);

    const _changePlayState = () => {
        if (playCrlEle.current) {
            playCrlEle.current.switchPlay();
        }
    };

    const [isFullScreen, setIsFullScreen] = useState(false);

    const checkFullScreen = () => {
        let isFull =
            document.webkitIsFullScreen ||
            document.mozFullScreen ||
            document.msFullscreenElement ||
            document.fullscreenElement;
        if (isFull == null || isFull == undefined) {
            isFull = false;
        }
        return isFull;
    };
    const _fullScreenClick = () => {
        if (checkFullScreen()) {
            setIsFullScreen(false);
            document.exitFullscreen();
        } else {
            videoBoxRef.current.requestFullscreen();
            setIsFullScreen(true);
        }
    };

    const _onReload = () => {
        setVideoUrl(FJUtil.getReloadNewUrl(url));
    };

    return (
        <>
            <FJVideoBox
                src={videoUrl}
                autoPlay={isAutoPlay}
                ref={videoBoxRef}
                onEnded={_endCallback}
                onLoadedData={_videoLoaded}
                onClick={_changePlayState}
                onError={onError}
                viewSize={viewSize}
                renderBottom={renderBottom}
                ratio={ratio}
                onReload={_onReload}
                style={style}
                isFullScreen={isFullScreen}
                isShowTransparentBG={isShowTransparentBG}
                ignoreViewSize={ignoreViewSize}
            >
                <div style={{ opacity: !hideController && isLoaded && !isLoadedError ? 1 : 0 }}>
                    <FJPlayController
                        ref={playCrlEle}
                        duration={videoDuration}
                        play={_play}
                        pause={_pause}
                        dataChange={_sliderDataChange}
                        previewVolumeChange={_volumeChange}
                        scope={scope}
                        fullScreenClick={_fullScreenClick}
                        isFullScreen={isFullScreen}
                        isKeySpace={isKeySpace}
                        isShowFullScreen={isShowFullScreen}
                        isShowVolume={true}
                    />
                </div>
            </FJVideoBox>
        </>
    );
};
export default FJVideoContent;
